import { useEffect, useMemo } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { omit } from 'lodash';

import { PageViewContextProvider } from '@/lib/analytics';
import { useAccountContext, useBuildAccountUrl } from '@/lib/accounts/context';
import { useKeyPress } from '@/lib/hooks/use-key-press';
import { FullPageLoader } from '@/components/loader';
import { useLayoutContext } from '@/components/layout/layout-context';
import { useExplorationsQuery } from '@/graphql';
import {
  buildModelExploration,
  getExplorationTabs,
  getNewTabs,
  replaceConversationId,
} from '@/core/exploration';

import {
  ExplorationStorageProvider,
  useExploration,
  useExplorationStorageContext,
} from './state-store';
import { ExplorationContextProvider } from './exploration/exploration-context';
import { ExplorationView } from './exploration';
import {
  decodeExplorationParamsHash,
  buildExplorationUrl,
  getParameters,
  getExplorationFromUrl,
  modelSupportsDetailExploration,
  getModelDetailExploration,
  getVariableDefinitions,
  getExplorationHashParams,
  getQueryVariablesFromParameters,
  getOtherHashParameters,
  TabParameter,
} from './utils';
import { MetadataContextProvider, useMetadataContext } from './metadata-context';
import { convertExplorationsArrayTypes, convertModelTypes } from './input';
import { useExplorationDelete, useExplorationUpsert } from './exploration/hooks';
import { ensureValidExploration } from './utils';
import { RedirectToDetailExploration } from './redirect-to-detail-exploration';
import { DirtyContextProvider } from './dirty-context';
import { ConversationRepositoryContextProvider } from './conversation/conversation-repository-context';
import { ExplorationUIContextProvider } from './exploration/exploration-ui-context';
import { Exploration, ExplorationParameters } from './types';
import { migrateExploration } from './exploration/migrations';

import styles from './explore.module.scss';

export const Explore = () => {
  const location = useLocation();
  const { account, isFeatureEnabled } = useAccountContext();

  const { data, loading, refetch } = useExplorationsQuery({
    variables: { accountId: account.accountId },
  });

  const { models, metrics, explorations } = useMemo(() => {
    const models = convertModelTypes(data?.account?.models) ?? [];
    const metrics = data?.account?.metrics ?? [];

    const savedDetailExplorations = convertExplorationsArrayTypes(
      data?.account?.parameterisedExplorations ?? [],
    );

    const modelDetailExplorations = models
      .filter(modelSupportsDetailExploration)
      .map(getModelDetailExploration)
      // Exclude detail explorations for models that have detail explorations customized and persisted
      .filter(({ explorationId }) =>
        savedDetailExplorations.every((e) => e.explorationId !== explorationId),
      );

    const explorations = [
      ...convertExplorationsArrayTypes(data?.account?.explorations ?? []),
      ...savedDetailExplorations,
      ...modelDetailExplorations,
      ...models.map((model) => buildModelExploration(model)),
    ].map((exploration) => ({
      ...exploration,
      options: { ...exploration.options, explorationSourceId: exploration.explorationId },
    }));

    return { models, metrics, explorations };
  }, [data?.account]);

  if (loading) {
    return <FullPageLoader />;
  }

  if (
    models.length === 0 &&
    (!isFeatureEnabled('exploreWithoutModels') || account.integrations.length === 0)
  ) {
    return (
      <div className={styles.empty}>
        <p>
          <img src="/logo.svg" width={200} />
        </p>
        <h2>Welcome! Let&apos;s get you exploring.</h2>
        <p>
          To start exploring your data, you&apos;ll first need to create a data model or add an
          integration.
          <br />
          Get started by{' '}
          <Link to="https://docs.supersimple.io/guides/setup/#data-model-setup" target="_blank">
            following along with our setup guide
          </Link>
          .
        </p>
      </div>
    );
  }

  const shouldRedirectToExploration = getExplorationHashParams(location.hash) !== null;

  return (
    <MetadataContextProvider models={models} explorations={explorations} metrics={metrics}>
      <ExplorationStorageProvider explorations={explorations}>
        <Routes>
          <Route path="detail" element={<RedirectToDetailExploration />} />
          <Route path=":explorationId" element={<ExplorationPage refetch={refetch} />} />
          <Route
            path="*"
            element={
              shouldRedirectToExploration ? (
                <ExplorationUrlLoader />
              ) : (
                <ExplorationPage refetch={refetch} />
              )
            }
          />
        </Routes>
      </ExplorationStorageProvider>
    </MetadataContextProvider>
  );
};

const ExplorationUrlLoader = () => {
  const buildAccountUrl = useBuildAccountUrl();
  const { models, explorations } = useMetadataContext();
  const { setExploration } = useExplorationStorageContext();
  const location = useLocation();

  const exploration = getExplorationFromUrl(location.hash, explorations, models);

  if (exploration !== null) {
    return (
      <Navigate to={buildAccountUrl(buildExplorationUrl(setExploration(exploration)))} replace />
    );
  }

  return <FullPageLoader />;
};

const ExplorationPage = ({ refetch }: { refetch: () => Promise<unknown> }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const buildAccountUrl = useBuildAccountUrl();

  const { upsertExploration } = useExplorationUpsert();
  const { deleteExploration } = useExplorationDelete();

  const { models, metrics } = useMetadataContext();
  const { exploration, setExploration, loading } = useExploration(params.explorationId);
  const { setActiveExplorationSourceId, scrollToTop } = useLayoutContext();

  useKeyPress(['y', 'ctrl', 'meta'], () => {
    navigate(1);
  });

  useKeyPress(['z', 'ctrl', 'meta'], (event) => {
    event.preventDefault();

    if (!event.shiftKey && exploration?.options?.previousStateId === undefined) {
      return;
    }

    const forward = event.shiftKey;
    navigate(forward ? 1 : -1);
  });

  // This is to ensure that when the user navigates to a new exploration, the page scrolls to the top
  useEffect(() => scrollToTop(), [exploration?.options?.explorationSourceId, scrollToTop]);

  if (loading) {
    return <FullPageLoader />;
  }

  const variableDefinitions = getVariableDefinitions(exploration);
  const parameters = getParameters(
    decodeExplorationParamsHash(location.hash) ?? {},
    variableDefinitions,
  );
  const variables = getQueryVariablesFromParameters(variableDefinitions, parameters);
  const migratedExploration = migrateExploration(exploration);
  const validExploration = ensureValidExploration(migratedExploration, models, metrics, variables);

  const tabs = getExplorationTabs(validExploration) ?? [];
  const activeTabId =
    tabs.find((tab) => tab.tabId === getOtherHashParameters(location.hash)[TabParameter])?.tabId ??
    tabs.at(0)?.tabId ??
    null;

  const handleSetExploration = (
    exploration: Exploration,
    parameters: ExplorationParameters | null,
  ) => {
    const hashParams = getOtherHashParameters(location.hash);
    const newExploration = setExploration(exploration);

    const createdTab = getNewTabs(validExploration, newExploration).at(-1);
    const updatedHashParams =
      createdTab !== undefined ? { ...hashParams, [TabParameter]: createdTab.tabId } : hashParams;

    navigate(buildAccountUrl(buildExplorationUrl(newExploration, parameters, updatedHashParams)));
    setActiveExplorationSourceId(newExploration.options?.explorationSourceId ?? null);
    return newExploration;
  };

  const handleSetActiveTabId = (tabId: string | null, replace = false) => {
    const hashParams = getOtherHashParameters(location.hash);
    const tabParams =
      tabId !== null ? { ...hashParams, [TabParameter]: tabId } : omit(hashParams, TabParameter);
    navigate(buildAccountUrl(buildExplorationUrl(validExploration, parameters, tabParams)), {
      replace,
    });
  };

  return (
    <PageViewContextProvider>
      <DirtyContextProvider>
        <ConversationRepositoryContextProvider
          exploration={exploration}
          parameters={parameters}
          onBranch={(oldConversationId, newConversationId) =>
            handleSetExploration(
              replaceConversationId(validExploration, oldConversationId, newConversationId),
              parameters,
            )
          }>
          <ExplorationUIContextProvider
            exploration={validExploration}
            parameters={parameters}
            activeTabId={activeTabId}
            setActiveTabId={handleSetActiveTabId}>
            <ExplorationContextProvider
              exploration={validExploration}
              models={models}
              metrics={metrics}
              setExploration={handleSetExploration}
              setParameters={(parameters2) => {
                return navigate(
                  buildAccountUrl(
                    buildExplorationUrl(
                      validExploration,
                      parameters2,
                      getOtherHashParameters(location.hash),
                    ),
                  ),
                );
              }}
              upsertExploration={async (exploration, parameters) => {
                const nextExploration = await upsertExploration(exploration);
                await refetch();

                navigate(
                  buildAccountUrl(
                    buildExplorationUrl(
                      nextExploration,
                      parameters,
                      getOtherHashParameters(location.hash),
                    ),
                  ),
                );

                return nextExploration;
              }}
              deleteExploration={async (explorationId: string) => {
                await deleteExploration(explorationId);
                await refetch();

                navigate(buildAccountUrl('/explore'));
              }}
              parameters={parameters}>
              <ExplorationView />
            </ExplorationContextProvider>
          </ExplorationUIContextProvider>
        </ConversationRepositoryContextProvider>
      </DirtyContextProvider>
    </PageViewContextProvider>
  );
};
